import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { apiClient } from "@/lib/api";
import { DynamicIcon } from "@/components/shared/IconMap";
import { useLanguage } from "@/i18n/LanguageContext";

const CountUp = ({ value, suffix }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState("0");
  const numeric = parseInt(String(value).replace(/[^0-9]/g, ""), 10) || 0;
  const hasComma = String(value).includes(",");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            const duration = 1400;
            const start = performance.now();
            const tick = (now) => {
              const progress = Math.min((now - start) / duration, 1);
              const current = Math.floor(progress * numeric);
              setDisplay(hasComma ? current.toLocaleString("en-US") : String(current));
              if (progress < 1) requestAnimationFrame(tick);
              else setDisplay(hasComma ? numeric.toLocaleString("en-US") : String(numeric));
            };
            requestAnimationFrame(tick);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [numeric, hasComma]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
};

export const StatsCounter = () => {
  const [stats, setStats] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    apiClient.get("/stats").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  if (!stats.length) return null;

  return (
    <section
      className="relative py-16 sm:py-20 overflow-hidden"
      style={{ background: "linear-gradient(120deg, #0F766E 0%, #0d5a54 100%)" }}
      data-testid="stats-section"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(700px 400px at 90% 0%, rgba(184,154,61,0.25), transparent 60%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-xs font-semibold tracking-[0.25em] uppercase text-white/70 mb-10">{t("stats.eyebrow")}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
          {stats.map((stat, idx) => (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`text-center ${idx < stats.length - 1 ? "lg:border-r lg:border-white/15" : ""}`}
              data-testid="stat-card"
            >
              <div className="h-10 w-10 rounded-full bg-white/10 grid place-items-center mx-auto mb-3">
                <DynamicIcon name={stat.icon} className="h-5 w-5 text-[color:var(--gold-400)]" />
              </div>
              <p className="text-4xl sm:text-5xl font-medium text-white font-heading">
                <CountUp value={stat.value} suffix={stat.suffix} />
              </p>
              <p className="mt-2 text-xs sm:text-sm text-white/75">{t(`stats.labels.${stat.label}`, stat.label)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
